'use client';

/**
 * Cross-platform messaging helpers. Abstracts:
 *   - Web (PWA / browser)        → `firebase/messaging`
 *   - iOS / Android (Capacitor)  → `@capacitor-firebase/messaging`
 *
 * The two SDKs return the same shape of FCM **registration token** which is
 * what the console stores in `NotificationDevice.token`.
 *
 * Permission UX rule: never call `requestPermission()` on page load — only
 * after a user gesture (button click). The `requestNotificationPermission()`
 * helper enforces this implicitly because it must be called from a click
 * handler.
 *
 * Foreground messages: with our **data-only** payload the OS does NOT show a
 * notification automatically when the app is in foreground. Callers should
 * subscribe with `onForegroundMessage` and render an in-app toast.
 */

import { Capacitor } from '@capacitor/core';
import { getFirebaseApp, FIREBASE_VAPID_KEY } from './config';

export type DevicePlatform = 'WEB' | 'IOS' | 'ANDROID';

export type IncomingMessage = {
  notificationId?: number;
  title: string;
  body: string;
  deepLink?: string;
  data: Record<string, string>;
};

export function getPlatformKind(): DevicePlatform {
  const p = Capacitor.getPlatform();
  if (p === 'ios') return 'IOS';
  if (p === 'android') return 'ANDROID';
  return 'WEB';
}

export function isPushSupported(): boolean {
  if (Capacitor.isNativePlatform()) return true;
  if (typeof window === 'undefined') return false;
  return 'Notification' in window && 'serviceWorker' in navigator;
}

export function currentPermission(): NotificationPermission | 'unsupported' {
  if (Capacitor.isNativePlatform()) {
    // Can't synchronously read native permission state without an async call,
    // so check whether we have a stored token as a proxy for "granted".
    // NOTE: prefer the async `checkPermission()` where possible — this proxy is
    // only reliable once a token has actually been registered & persisted.
    if (typeof window !== 'undefined') {
      const storedToken = window.localStorage.getItem('docupro:fcm:lastRegisteredToken');
      if (storedToken) return 'granted';
    }
    return 'default';
  }
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

/**
 * Async permission check that reads the *real* OS permission state on native
 * (via `FirebaseMessaging.checkPermissions()`), and `Notification.permission`
 * on web. Use this instead of `currentPermission()` whenever you can await —
 * the sync version relies on a localStorage token proxy that is wrong until a
 * token has been registered.
 */
export async function checkPermission(): Promise<NotificationPermission | 'unsupported'> {
  if (Capacitor.isNativePlatform()) {
    const { FirebaseMessaging } = await loadNativeMessaging();
    if (!FirebaseMessaging) return 'unsupported';
    try {
      const res = await FirebaseMessaging.checkPermissions();
      if (res.receive === 'granted') return 'granted';
      if (res.receive === 'denied') return 'denied';
      return 'default';
    } catch (e) {
      console.error('[messaging] checkPermissions failed', e);
      return 'default';
    }
  }
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

/**
 * Must be called from a user gesture handler (click). Returns the granted
 * permission state on web; on native, returns 'granted' if the user accepts.
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (Capacitor.isNativePlatform()) {
    const { FirebaseMessaging } = await loadNativeMessaging();
    if (!FirebaseMessaging) return 'denied';
    const res = await FirebaseMessaging.requestPermissions();
    return res.receive === 'granted' ? 'granted' : 'denied';
  }
  if (typeof window === 'undefined' || !('Notification' in window)) return 'denied';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  return Notification.requestPermission();
}

async function getOrRegisterSwRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return null;
  try {
    let reg = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
    if (!reg) {
      reg = await navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: '/' });
    }
    if (reg.active) return reg;
    await new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error('SW activation timed out')), 10_000);
      const sw = reg!.installing || reg!.waiting;
      if (!sw) {
        clearTimeout(timeout);
        if (reg!.active) return resolve();
        return reject(new Error('SW has no installing/waiting/active worker'));
      }
      sw.addEventListener('statechange', () => {
        if (sw.state === 'activated') {
          clearTimeout(timeout);
          resolve();
        } else if (sw.state === 'redundant') {
          clearTimeout(timeout);
          reject(new Error('SW became redundant'));
        }
      });
    });
    return reg;
  } catch (e) {
    console.error('[messaging] SW register failed', e);
    return null;
  }
}

async function loadNativeMessaging(): Promise<{ FirebaseMessaging?: any }> {
  try {
    // Loaded dynamically so the web build doesn't require the plugin.
    const mod = await import('@capacitor-firebase/messaging');
    return { FirebaseMessaging: (mod as any).FirebaseMessaging };
  } catch {
    return {};
  }
}

/**
 * Returns an FCM registration token, or null if push is not configured /
 * permission denied / SDK unavailable.
 */
export async function getFcmToken(): Promise<string | null> {
  if (Capacitor.isNativePlatform()) {
    const { FirebaseMessaging } = await loadNativeMessaging();
    if (!FirebaseMessaging) return null;
    // The very first time permission is granted, the OS is still completing
    // APNS/FCM device registration in the background, so an immediate
    // getToken() often throws or returns null — which made "Enable" fail on
    // the first tap and only succeed on the second. Retry a few times with a
    // short backoff to ride out that registration window.
    for (let attempt = 0; attempt < 5; attempt++) {
      try {
        const res = await FirebaseMessaging.getToken();
        if (res?.token) return res.token;
      } catch (e) {
        console.error(`[messaging] native getToken failed (attempt ${attempt + 1})`, e);
      }
      await new Promise((resolve) => setTimeout(resolve, 500 * (attempt + 1)));
    }
    return null;
  }

  const app = getFirebaseApp();
  if (!app) return null;
  if (!FIREBASE_VAPID_KEY) {
    console.warn('[messaging] NEXT_PUBLIC_FIREBASE_VAPID_KEY missing — web push disabled.');
    return null;
  }
  if (currentPermission() !== 'granted') return null;

  try {
    const { getMessaging, getToken, isSupported } = await import('firebase/messaging');
    if (!(await isSupported())) return null;
    const swReg = await getOrRegisterSwRegistration();
    if (!swReg) return null;
    const messaging = getMessaging(app);
    const token = await getToken(messaging, {
      vapidKey: FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: swReg,
    });
    return token || null;
  } catch (e) {
    console.error('[messaging] web getToken failed', e);
    return null;
  }
}

/**
 * Subscribes to foreground messages and returns an unsubscribe fn.
 * Caller is responsible for rendering the in-app toast / updating the feed.
 */
export async function onForegroundMessage(
  cb: (msg: IncomingMessage) => void
): Promise<() => void> {
  if (Capacitor.isNativePlatform()) {
    const { FirebaseMessaging } = await loadNativeMessaging();
    if (!FirebaseMessaging) return () => undefined;
    const handle = await FirebaseMessaging.addListener('notificationReceived', (event: any) => {
      const data = event?.notification?.data ?? {};
      cb({
        notificationId: data.notificationId ? Number(data.notificationId) : undefined,
        title: data.title ?? event?.notification?.title ?? '',
        body: data.body ?? event?.notification?.body ?? '',
        deepLink: data.deepLink,
        data,
      });
    });
    return () => handle.remove();
  }

  const app = getFirebaseApp();
  if (!app) return () => undefined;
  try {
    const { getMessaging, onMessage, isSupported } = await import('firebase/messaging');
    if (!(await isSupported())) return () => undefined;
    const messaging = getMessaging(app);
    const unsub = onMessage(messaging, (payload) => {
      const data = (payload.data ?? {}) as Record<string, string>;
      cb({
        notificationId: data.notificationId ? Number(data.notificationId) : undefined,
        title: data.title ?? payload.notification?.title ?? '',
        body: data.body ?? payload.notification?.body ?? '',
        deepLink: data.deepLink,
        data,
      });
    });
    return unsub;
  } catch (e) {
    console.error('[messaging] onForegroundMessage failed', e);
    return () => undefined;
  }
}

/**
 * Subscribes to user taps on background notifications (deep-link routing).
 * On web, background taps are handled inside the service worker itself.
 */
export async function onNotificationTap(
  cb: (msg: IncomingMessage) => void
): Promise<() => void> {
  if (Capacitor.isNativePlatform()) {
    const { FirebaseMessaging } = await loadNativeMessaging();
    if (!FirebaseMessaging) return () => undefined;
    const handle = await FirebaseMessaging.addListener(
      'notificationActionPerformed',
      (event: any) => {
        const data = event?.notification?.data ?? {};
        cb({
          notificationId: data.notificationId ? Number(data.notificationId) : undefined,
          title: data.title ?? event?.notification?.title ?? '',
          body: data.body ?? event?.notification?.body ?? '',
          deepLink: data.deepLink,
          data,
        });
      }
    );
    return () => handle.remove();
  }
  return () => undefined;
}
